import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  Container,
  Paper,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton
} from '@mui/material';
import {
  LocalShipping,
  CheckCircle,
  AccessTime,
  ArrowForward,
  PhotoCamera,
  Inventory,
  MoveToInbox,
  LocalShippingOutlined
} from '@mui/icons-material';
import api from '../services/api';
import { brand, status as statusColors, text, shadow } from '../theme/colors';

const STATUS_FLOW = {
  confirmed: { next: 'preparing', label: 'Start Preparing', icon: <Inventory /> },
  preparing: { next: 'ready_for_delivery', label: 'Mark Ready', icon: <MoveToInbox /> },
  ready_for_delivery: { next: 'in_transit', label: 'Mark In Transit', icon: <LocalShippingOutlined /> }
};

const MerchantOrdersPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [updatingOrderId, setUpdatingOrderId] = useState(null);
  // Get filter from URL query params, default to 'active'
  const [filter, setFilter] = useState(searchParams.get('filter') || 'active');

  useEffect(() => {
    // Update filter if URL param changes
    const urlFilter = searchParams.get('filter');
    if (urlFilter && urlFilter !== filter) {
      setFilter(urlFilter);
    }
  }, [searchParams]);

  useEffect(() => {
    loadOrders();
  }, [filter]);

  const loadOrders = async () => {
    try {
      setLoading(true);
      setError('');

      const params = {};
      if (filter === 'active') {
        // Exclude delivered, picked_up, completed, cancelled, disputed from active orders
        params.excludeStatus = 'delivered,picked_up,completed,cancelled,disputed';
      } else if (filter === 'picked_up') {
        // For picked_up filter, backend will include both 'picked_up' status and 'completed' orders with pickup delivery method
        params.status = filter;
      } else if (filter !== 'all') {
        params.status = filter;
      }
      // When filter is 'all', no params are set, so all orders are returned

      const response = await api.get(
        '/material-orders',
        {
          params
        }
      );

      if (response.data.success) {
        setOrders(response.data.data || []);
      }
    } catch (err) {
      console.error('Error loading orders:', err);
      setError('Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return 'info';
      case 'preparing': return 'warning';
      case 'ready_for_delivery': return 'primary';
      case 'delivered': return 'success';
      case 'picked_up': return 'success';
      case 'completed': return 'success';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 600 }}>
          Material Orders
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant={filter === 'active' ? 'contained' : 'outlined'}
            onClick={() => setFilter('active')}
            size="small"
          >
            Active Orders
          </Button>
          <Button
            variant={filter === 'confirmed' ? 'contained' : 'outlined'}
            onClick={() => setFilter('confirmed')}
            size="small"
          >
            Confirmed
          </Button>
          <Button
            variant={filter === 'delivered' ? 'contained' : 'outlined'}
            onClick={() => setFilter('delivered')}
            size="small"
          >
            Delivered
          </Button>
          <Button
            variant={filter === 'picked_up' ? 'contained' : 'outlined'}
            onClick={() => setFilter('picked_up')}
            size="small"
          >
            Picked Up
          </Button>
          <Button
            variant={filter === 'completed' ? 'contained' : 'outlined'}
            onClick={() => setFilter('completed')}
            size="small"
          >
            Completed
          </Button>
          <Button
            variant={filter === 'all' ? 'contained' : 'outlined'}
            onClick={() => setFilter('all')}
            size="small"
          >
            All Orders
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {orders.length === 0 ? (
        <Paper elevation={2} sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            No orders found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Orders from clients will appear here once they place orders from your quotes.
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={2}>
          {orders.map((order) => (
            <Grid item xs={12} key={order.id}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                        Order #{order.id}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Client: {order.client_name || 'N/A'} | Project: {order.project_title || 'N/A'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Total: ${parseFloat(order.total_amount || 0).toFixed(2)}
                      </Typography>
                    </Box>
                    <Chip
                      label={order.status.replace('_', ' ')}
                      color={getStatusColor(order.status)}
                    />
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Items:</Typography>
                    {order.items?.slice(0, 3).map((item, index) => (
                      <Typography key={index} variant="body2" color="text.secondary">
                        • {item.material_name} ({item.quantity} {item.unit})
                      </Typography>
                    ))}
                    {order.items?.length > 3 && (
                      <Typography variant="body2" color="text.secondary">
                        ... and {order.items.length - 3} more
                      </Typography>
                    )}
                  </Box>

                  {/* Status Progress Bar */}
                  {['confirmed', 'preparing', 'ready_for_delivery', 'in_transit'].includes(order.status) && (
                    <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {['confirmed', 'preparing', 'ready_for_delivery', 'in_transit'].map((step, idx) => {
                        const stepIndex = ['confirmed', 'preparing', 'ready_for_delivery', 'in_transit'].indexOf(order.status);
                        const isComplete = idx <= stepIndex;
                        const isCurrent = idx === stepIndex;
                        return (
                          <React.Fragment key={step}>
                            <Box sx={{
                              flex: 1, height: 4, borderRadius: 2,
                              bgcolor: isComplete ? brand.primary : '#e2e8f0',
                              transition: 'background-color 0.3s'
                            }} />
                          </React.Fragment>
                        );
                      })}
                      <Typography variant="caption" sx={{ ml: 1, color: text.secondary, whiteSpace: 'nowrap', fontWeight: 500 }}>
                        Step {['confirmed', 'preparing', 'ready_for_delivery', 'in_transit'].indexOf(order.status) + 1}/4
                      </Typography>
                    </Box>
                  )}

                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                    {/* Next status action button */}
                    {STATUS_FLOW[order.status] && (
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={updatingOrderId === order.id ? <CircularProgress size={14} color="inherit" /> : STATUS_FLOW[order.status].icon}
                        disabled={updatingOrderId === order.id}
                        onClick={async (e) => {
                          e.stopPropagation();
                          try {
                            setUpdatingOrderId(order.id);
                            setError('');
                            await api.patch(`/material-orders/${order.id}/status`, {
                              status: STATUS_FLOW[order.status].next
                            });
                            loadOrders();
                          } catch (err) {
                            setError(err.response?.data?.error || 'Failed to update status');
                          } finally {
                            setUpdatingOrderId(null);
                          }
                        }}
                        sx={{
                          background: brand.gradient,
                          fontWeight: 600,
                          boxShadow: shadow.sm,
                          transition: 'transform 0.15s',
                          '&:hover': { background: brand.gradientHover, transform: 'translateY(-1px)' }
                        }}
                      >
                        {STATUS_FLOW[order.status].label}
                      </Button>
                    )}
                    {/* Submit delivery/pickup — available from in_transit or ready_for_delivery */}
                    {(order.status === 'in_transit' || order.status === 'ready_for_delivery') && (
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<LocalShipping />}
                        onClick={() => navigate(`/orders/${order.id}/delivery/submit`)}
                        sx={{
                          background: brand.gradientWarm,
                          fontWeight: 600,
                          '&:hover': { background: brand.gradientEarth }
                        }}
                      >
                        {order.delivery_method === 'pickup' ? 'Record Pickup' : 'Record Delivery'}
                      </Button>
                    )}
                    {(order.status === 'delivered' || order.status === 'picked_up') && (
                      <Chip
                        label="Awaiting Client Confirmation"
                        color="warning"
                        size="small"
                      />
                    )}
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => navigate(`/orders/${order.id}`)}
                    >
                      View Details
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Container>
  );
};

export default MerchantOrdersPage;

